import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './client';
// Tiny error surfacing helper for mutations. The proper toast system is a
// follow-up; in the meantime we at least make failures visible instead of
// dropping them silently into the console. Extracts FastAPI's `{detail: ...}`
// out of ApiError.body when present.
function showMutationError(e, fallback) {
    let msg = fallback;
    if (e instanceof ApiError) {
        const body = e.body;
        const detail = body && typeof body === 'object' ? body.detail : null;
        msg = typeof detail === 'string' ? detail : `${e.status} ${e.message}`;
    }
    else if (e instanceof Error) {
        msg = e.message;
    }
    // eslint-disable-next-line no-alert
    alert(msg);
}
// Backend returns Page<DatabaseOut> = {items, total, limit, offset}; unwrap to items[].
// 10s polling so the list reflects backups going queued → running → succeeded
// without manual refresh. Mutations invalidate immediately; this covers passive
// transitions (cron, worker outcomes). Paused in hidden tabs.
export function useDatabases() {
    return useQuery({
        queryKey: ['databases'],
        queryFn: async () => (await api('/databases')).items,
        refetchInterval: 10_000,
        refetchIntervalInBackground: false,
    });
}
export function useDatabase(id) {
    return useQuery({
        queryKey: ['databases', id],
        queryFn: () => api(`/databases/${id}`),
        enabled: !!id,
        refetchInterval: 10_000,
        refetchIntervalInBackground: false,
    });
}
export function useCreateDatabase() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => api('/databases', { method: 'POST', body: input }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['databases'] }),
    });
}
export function useUpdateDatabase(id) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (input) => api(`/databases/${id}`, { method: 'PATCH', body: input }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['databases'] });
            qc.invalidateQueries({ queryKey: ['databases', id] });
        },
    });
}
export function useDeleteDatabase() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => api(`/databases/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['databases'] }),
    });
}
// Backend: POST /api/v1/backups enqueues a manual backup job for one database.
export function useRunBackupNow() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (databaseId) => api(`/backups`, {
            method: 'POST',
            body: { database_id: databaseId },
        }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['jobs'] });
            qc.invalidateQueries({ queryKey: ['databases'] });
        },
        onError: (e) => showMutationError(e, 'Failed to start backup'),
    });
}
// Backend has no dedicated /pause endpoint; pausing is `enabled = !paused` via PATCH.
export function usePauseDatabase() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, paused }) => api(`/databases/${id}`, {
            method: 'PATCH',
            body: { enabled: !paused },
        }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['databases'] }),
    });
}
// Form-time test: caller supplies plain creds (used by the create/edit form
// before save).
export function useTestConnection() {
    return useMutation({
        mutationFn: (input) => api(`/databases/test-connection`, {
            method: 'POST',
            body: input,
        }),
    });
}
// Existing-record test as a query: fires automatically when the DB detail
// page mounts; refetches on window focus by default. Use the form-time hook
// (useTestConnection) for "test before save" flows.
export function useExistingConnectionTest(id) {
    return useQuery({
        queryKey: ['db-test', id],
        queryFn: () => api(`/databases/${id}/test-connection`, {
            method: 'POST',
        }),
        enabled: !!id,
        retry: false,
        gcTime: 60_000,
        staleTime: 0,
    });
}
